import * as THREE from 'three';

/**
 * Three.js Interactive Animated Dot Matrix Grid Scene
 * Renders a precision orthogonal dot matrix grid across dark background sections.
 * Features:
 * - Anti-aliased circular points with custom GPU GLSL shader
 * - Organic 3D wave ripples and subtle breathing oscillations
 * - Active glowing white node that tracks mouse interactions and roams along grid lines when idle
 * - Radial illumination and bloom falloff on nearby matrix points
 * - Viewport intersection observer to sleep when scrolled out of view for maximum 60-120fps performance
 */
export class DarkDotGridScene {
  constructor(canvasId = 'dark-grid-canvas') {
    this.canvas = typeof canvasId === 'string' ? document.getElementById(canvasId) : canvasId;
    if (!this.canvas) return;

    this.container = this.canvas.parentElement;
    this.time = 0;
    this.clock = new THREE.Clock();

    // Mouse & Idle Node Coordinates
    this.mouseWorld = new THREE.Vector2(0, 0);
    this.targetMouseWorld = new THREE.Vector2(0, 0);
    this.mouseActive = 0;
    this.targetMouseActive = 0;
    this.lastMouseMoveTime = 0;

    this.idlePoint = new THREE.Vector2(0, 0);
    this.parallax = { x: 0, y: 0, targetX: 0, targetY: 0 };

    this.isVisible = false;
    this.animationFrameId = null;

    this.init();
  }

  init() {
    // 1. Scene & Camera
    this.scene = new THREE.Scene();
    const width = this.getWidth();
    const height = this.getHeight();

    this.camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 1000);
    this.camera.position.set(0, 0, 75);

    // 2. WebGL Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    // 3. Build Dot Matrix Points Mesh
    this.createDotMatrix();

    // 4. Event Listeners
    this.handleResize = this.onResize.bind(this);
    this.handleMouseMove = this.onMouseMove.bind(this);

    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('mousemove', this.handleMouseMove, {
      passive: true,
    });

    // Page visibility to sleep rendering when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      } else if (!this.animationFrameId) {
        this.animate();
      }
    });

    // 5. Scroll-linked opacity fade from Hero into dark sections
    this.initScrollFade();

    // Initial render call
    this.isVisible = true;
    this.animate();
  }

  getWidth() {
    return window.innerWidth;
  }

  getHeight() {
    return window.innerHeight;
  }

  onResize() {
    if (!this.camera || !this.renderer || !this.canvas) return;
    const width = this.getWidth();
    const height = this.getHeight();

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);

    if (this.uniforms && this.uniforms.uPixelRatio) {
      this.uniforms.uPixelRatio.value = dpr;
    }

    if (this.pointsMesh) {
      this.scene.remove(this.pointsMesh);
      if (this.pointsMesh.geometry) this.pointsMesh.geometry.dispose();
      if (this.pointsMesh.material) this.pointsMesh.material.dispose();
      this.createDotMatrix();
    }
  }

  createDotMatrix() {
    // Determine world viewport boundaries at camera Z = 75
    const vFov = (this.camera.fov * Math.PI) / 180;
    const worldHeight = 2 * Math.tan(vFov / 2) * this.camera.position.z;
    const worldWidth = worldHeight * this.camera.aspect;

    // Grid spacing in world units (~32-36px visual pitch)
    const spacing = 2.4;
    const margin = 12.0;

    const cols = Math.ceil((worldWidth + margin * 2) / spacing);
    const rows = Math.ceil((worldHeight + margin * 2) / spacing);
    const totalPoints = cols * rows;

    const positions = new Float32Array(totalPoints * 3);
    const baseAlphas = new Float32Array(totalPoints);
    const phases = new Float32Array(totalPoints);

    const startX = -((cols - 1) * spacing) / 2;
    const startY = -((rows - 1) * spacing) / 2;

    let idx = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = startX + c * spacing;
        const y = startY + r * spacing;
        const z = 0;

        positions[idx * 3] = x;
        positions[idx * 3 + 1] = y;
        positions[idx * 3 + 2] = z;

        // Subtle baseline alpha (0.16 to 0.22)
        baseAlphas[idx] = 0.18 + ((c + r) % 3) * 0.02;
        phases[idx] = (c * 0.15 + r * 0.12) % (Math.PI * 2);

        idx++;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aBaseAlpha', new THREE.BufferAttribute(baseAlphas, 1));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    // Custom GPU Shader for crisp anti-aliased dots & luminous active nodes
    const vertexShader = `
      uniform float uTime;
      uniform vec2 uMouse;
      uniform float uMouseActive;
      uniform vec2 uIdlePoint;
      uniform float uPixelRatio;

      attribute float aBaseAlpha;
      attribute float aPhase;

      varying float vIntensity;
      varying float vAlpha;

      void main() {
        vec3 pos = position;

        // 1. Gentle continuous 3D breathing wave across matrix
        float wave = sin(pos.x * 0.08 + uTime * 0.85 + aPhase) * cos(pos.y * 0.08 + uTime * 0.7);
        pos.z += wave * 1.8;

        // 2. Mouse cursor interactive radial illumination
        float distMouse = distance(pos.xy, uMouse);
        float mouseGlow = smoothstep(16.0, 0.0, distMouse) * uMouseActive;

        // 3. Autonomous wandering glowing node (matches screenshot bright point)
        float distIdle = distance(pos.xy, uIdlePoint);
        float idleGlow = smoothstep(11.0, 0.0, distIdle) * (1.0 - uMouseActive * 0.65);

        float totalGlow = max(mouseGlow, idleGlow);

        // Soft elevation towards camera for active nodes
        pos.z += totalGlow * 3.2;

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mvPosition;

        // Size scaling: standard dot is ~3.2px, active glowing node expands to ~10px
        float baseSize = 3.4 * uPixelRatio;
        float activeSize = 10.5 * uPixelRatio;
        gl_PointSize = mix(baseSize, activeSize, pow(totalGlow, 1.6)) * (65.0 / -mvPosition.z);

        vIntensity = totalGlow;
        vAlpha = mix(aBaseAlpha, 1.0, totalGlow);
      }
    `;

    const fragmentShader = `
      varying float vIntensity;
      varying float vAlpha;

      void main() {
        // Transform square point sprite into perfect anti-aliased circular dot
        vec2 coord = gl_PointCoord - vec2(0.5);
        float dist = length(coord);
        if (dist > 0.5) discard;

        float edge = 1.0 - smoothstep(0.40, 0.5, dist);
        float core = 1.0 - smoothstep(0.0, 0.25, dist);

        // Palette: Subtle pearl-white base dots, radiant pure white illuminated node
        vec3 baseColor = vec3(0.88, 0.90, 0.96);
        vec3 activeColor = vec3(1.0, 1.0, 1.0);
        vec3 color = mix(baseColor, activeColor, vIntensity);

        // Luminous halo bloom for active node
        color += vec3(0.25, 0.3, 0.35) * core * vIntensity;

        gl_FragColor = vec4(color, edge * vAlpha);
      }
    `;

    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(-999, -999) },
      uMouseActive: { value: 0 },
      uIdlePoint: { value: new THREE.Vector2(0, 0) },
      uPixelRatio: {
        value: Math.min(window.devicePixelRatio || 1, 2),
      },
    };

    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.pointsMesh = new THREE.Points(geometry, material);
    this.scene.add(this.pointsMesh);
  }

  initScrollFade() {
    if (!this.canvas) return;

    const hero = document.getElementById('intro') || document.getElementById('hero');
    if (!hero) {
      this.canvas.style.opacity = '1';
      return;
    }

    const updateOpacity = () => {
      const heroHeight = hero.offsetHeight || window.innerHeight;
      const scrollY = window.scrollY || window.pageYOffset || 0;

      // Smoothly fade in as user scrolls past Hero into dark sections
      const startFade = heroHeight * 0.3;
      const fullFade = heroHeight * 0.8;

      if (scrollY <= startFade) {
        this.canvas.style.opacity = '0';
      } else if (scrollY >= fullFade) {
        this.canvas.style.opacity = '1';
      } else {
        const ratio = (scrollY - startFade) / (fullFade - startFade);
        this.canvas.style.opacity = ratio.toFixed(2);
      }
    };

    this.handleScroll = updateOpacity.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    updateOpacity();
  }

  onMouseMove(e) {
    if (!this.canvas) return;

    const normX = (e.clientX / window.innerWidth) * 2 - 1;
    const normY = -((e.clientY / window.innerHeight) * 2 - 1);

    // Convert normalized screen coord to world plane coord at z=0
    const vFov = (this.camera.fov * Math.PI) / 180;
    const worldH = 2 * Math.tan(vFov / 2) * this.camera.position.z;
    const worldW = worldH * this.camera.aspect;

    this.targetMouseWorld.x = (normX * worldW) / 2;
    this.targetMouseWorld.y = (normY * worldH) / 2;
    this.targetMouseActive = 1.0;
    this.lastMouseMoveTime = window.performance ? window.performance.now() : Date.now();

    // Subtle 3D tilt
    this.parallax.targetX = normX * 0.04;
    this.parallax.targetY = normY * 0.04;
  }

  animate() {
    if (!this.isVisible) return;

    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

    const delta = this.clock.getDelta();
    this.time += delta;

    // Smooth spring damping towards mouse world target
    this.mouseWorld.lerp(this.targetMouseWorld, 0.08);

    // Auto-fade mouse activity if idle for >2.5s
    const now = window.performance ? window.performance.now() : Date.now();
    if (now - this.lastMouseMoveTime > 2500) {
      this.targetMouseActive = 0.0;
    }
    this.mouseActive += (this.targetMouseActive - this.mouseActive) * 0.06;

    // Organic wandering path for the autonomous glowing node when idle
    const t = this.time * 0.55;
    this.idlePoint.x = Math.sin(t * 0.7) * 26.0 + Math.sin(t * 1.5) * 8.0 + Math.cos(t * 0.3) * 6.0;
    this.idlePoint.y = Math.cos(t * 0.6) * 12.0 + Math.sin(t * 1.1) * 5.0;

    // Parallax damping
    this.parallax.x += (this.parallax.targetX - this.parallax.x) * 0.05;
    this.parallax.y += (this.parallax.targetY - this.parallax.y) * 0.05;

    if (this.pointsMesh) {
      this.pointsMesh.rotation.y = this.parallax.x;
      this.pointsMesh.rotation.x = -this.parallax.y;
    }

    // Update uniforms
    if (this.uniforms) {
      this.uniforms.uTime.value = this.time;
      this.uniforms.uMouse.value.copy(this.mouseWorld);
      this.uniforms.uMouseActive.value = this.mouseActive;
      this.uniforms.uIdlePoint.value.copy(this.idlePoint);
    }

    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('mousemove', this.handleMouseMove);
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }

    if (this.observer && this.canvas) {
      this.observer.unobserve(this.canvas);
    }

    if (this.pointsMesh) {
      this.scene.remove(this.pointsMesh);
      if (this.pointsMesh.geometry) this.pointsMesh.geometry.dispose();
      if (this.pointsMesh.material) this.pointsMesh.material.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}
